
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { GamePlan } from '@/types/gamePlanner';
import { Node, Edge } from '@xyflow/react';
import { offlineGamePlanService } from '@/services/gamePlanner/offlineGamePlanService';
import { useNetworkStatus } from '@/contexts/offline';

export function usePlanSaving({
  currentPlan,
  nodes,
  edges,
  onSaved
}: {
  currentPlan: GamePlan | null;
  nodes: Node[];
  edges: Edge[];
  onSaved?: () => void;
}) {
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  const { isOnline } = useNetworkStatus();

  const handleSaveClick = () => {
    if (currentPlan) {
      // If working with a template, always show save dialog to create a new plan
      if (currentPlan.is_template) {
        setShowSaveDialog(true);
      } else if (currentPlan.id) {
        // If editing a non-template existing plan, update directly
        handleUpdateExistingPlan();
      } else {
        // If creating a new plan, show the save dialog
        setShowSaveDialog(true);
      }
    } else {
      // If no current plan (new plan), show the save dialog
      setShowSaveDialog(true);
    }
  };

  const handleSaveCancel = () => {
    setShowSaveDialog(false);
  };

  const handleUpdateExistingPlan = async () => {
    if (!currentPlan) return;

    setIsSaving(true);
    try {
      // Update the flow data in the current plan
      const updatedPlan = {
        ...currentPlan,
        flow_data: { nodes, edges }
      };

      // Use offline service for updates
      await offlineGamePlanService.updateGamePlan(updatedPlan);

      const successMessage = isOnline
        ? 'Your game plan has been updated!'
        : 'Your game plan has been saved offline and will sync when you\'re back online!';

      toast({
        title: 'Success',
        description: successMessage
      });

      if (onSaved) {
        onSaved();
      }
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update your game plan',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveConfirm = async (name: string, description: string) => {
    setIsSaving(true);
    try {
      // Always save as a new game plan when working from a template
      await offlineGamePlanService.createGamePlan({
        name,
        description,
        nodes,
        edges,
        isTemplate: false,
        isPublic: false
      });

      const successMessage = isOnline
        ? 'Your game plan has been saved!'
        : 'Your game plan has been saved offline and will sync when you\'re back online!';

      toast({
        title: 'Success',
        description: successMessage
      });

      if (onSaved) {
        onSaved();
      }
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save your game plan',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
      setShowSaveDialog(false);
    }
  };

  return {
    showSaveDialog,
    setShowSaveDialog,
    isSaving,
    handleSaveClick,
    handleSaveCancel,
    handleSaveConfirm
  };
}
